<script setup>
// Auditoria → Configuração: pesos dos critérios, roteiro de atendimento, se o
// atendente vê as próprias avaliações e o envio do relatório por e-mail.
// Ligar a análise e os preços do custo real ficam no Super Admin.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createGestaoApi } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const form = ref(null);
const info = ref(null);
const carregando = ref(true);
const salvando = ref(false);
const erro = ref('');
const salvo = ref(false);

const preenche = data => {
  info.value = { ativa: data.ativa, modelo: data.modelo };
  form.value = {
    pesos: data.pesos.map(peso => ({ ...peso })),
    roteiro: data.roteiro,
    atendente_ve: data.atendente_ve,
    relatorio_envia: data.relatorio_envia,
    relatorio_emails: data.relatorio_emails,
  };
};

const carrega = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await api.value.configuracao();
    preenche(data);
  } catch (e) {
    erro.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
  } finally {
    carregando.value = false;
  }
};
onMounted(carrega);

const total = computed(() =>
  (form.value?.pesos || []).reduce((soma, peso) => soma + Number(peso.peso || 0), 0)
);

const salva = async () => {
  salvando.value = true;
  erro.value = '';
  salvo.value = false;
  try {
    const { data } = await api.value.salvaConfiguracao({
      pesos: Object.fromEntries(form.value.pesos.map(peso => [peso.chave, Number(peso.peso || 0)])),
      roteiro: form.value.roteiro,
      atendente_ve: form.value.atendente_ve,
      relatorio_envia: form.value.relatorio_envia,
      relatorio_emails: form.value.relatorio_emails,
    });
    preenche(data);
    salvo.value = true;
  } catch (e) {
    erro.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="carregando" class="flex justify-center py-10"><Spinner /></div>
    <p v-else-if="!form" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <form v-else class="flex flex-col gap-6" @submit.prevent="salva">
      <p
        class="p-3 m-0 text-sm rounded-xl"
        :class="info.ativa ? 'bg-n-teal-2 text-n-teal-11' : 'bg-n-amber-2 text-n-amber-11'"
      >
        {{ info.ativa ? t('NEUXS_GESTAO.CONFIG.LIGADA', { modelo: info.modelo }) : t('NEUXS_GESTAO.CONFIG.DESLIGADA') }}
      </p>

      <section class="flex flex-col gap-3">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CONFIG.CRITERIOS') }}</h2>
        <p class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.CONFIG.CRITERIOS_DICA') }}</p>
        <div
          v-for="peso in form.pesos"
          :key="peso.chave"
          class="grid grid-cols-[minmax(0,14rem)_1fr_3.5rem] items-center gap-3 text-sm"
        >
          <label :for="`peso-${peso.chave}`" class="text-n-slate-12">{{ peso.nome }}</label>
          <input :id="`peso-${peso.chave}`" v-model.number="peso.peso" type="range" min="0" max="40" step="1" class="!mb-0" />
          <span class="text-end tabular-nums text-n-slate-11">
            {{ total ? Math.round((100 * peso.peso) / total) : 0 }}%
          </span>
        </div>
      </section>

      <section class="flex flex-col gap-2">
        <label for="roteiro" class="text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CONFIG.ROTEIRO') }}</label>
        <p class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.CONFIG.ROTEIRO_DICA') }}</p>
        <textarea id="roteiro" v-model="form.roteiro" rows="7" maxlength="5000" class="!mb-0" />
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.atendente_ve" type="checkbox" class="!mb-0" />
          {{ t('NEUXS_GESTAO.CONFIG.ATENDENTE_VE') }}
        </label>
      </section>

      <section class="flex flex-col gap-2">
        <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.CONFIG.ENVIO') }}</h2>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.relatorio_envia" type="checkbox" class="!mb-0" />
          {{ t('NEUXS_GESTAO.CONFIG.ENVIA_SOZINHO') }}
        </label>
        <label for="relatorio-emails" class="text-sm text-n-slate-12">{{ t('NEUXS_GESTAO.CONFIG.EMAILS') }}</label>
        <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.CONFIG.EMAILS_DICA') }}</p>
        <textarea id="relatorio-emails" v-model="form.relatorio_emails" rows="3" class="!mb-0" />
      </section>

      <div class="flex items-center gap-3">
        <Button type="submit" :label="t('NEUXS_GESTAO.CONFIG.SALVAR')" :is-loading="salvando" />
        <span v-if="salvo" class="text-sm text-n-teal-11">{{ t('NEUXS_GESTAO.CONFIG.SALVO') }}</span>
        <span v-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</span>
      </div>
    </form>
  </div>
</template>
